import React from 'react';
import { Typography, Paper, Box, Divider, List, ListItem, ListItemText } from '@mui/material';

const SeveranceExplained = () => {
  return (
    <Paper sx={{ p: 3, mt: 3, mb: 3, maxWidth: '100%', bgcolor: 'rgba(0,0,0,0.7)' }}>
      <Box 
        sx={{ 
          height: '200px', 
          width: '100%', 
          bgcolor: '#f44336', 
          borderRadius: 1, 
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Typography variant="h4" color="white">
          Severance Pay Guide
        </Typography>
      </Box>
      
      <Typography variant="h4" gutterBottom align="left">
        Federal Severance Pay: A Comprehensive Guide
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom align="left">
        Understanding how federal severance pay is calculated, who is eligible, and what to expect when facing a Reduction in Force.
      </Typography>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="h5" gutterBottom sx={{ mt: 3 }} align="left">
        Eligibility for Federal Severance Pay
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        Federal employees facing job loss through a Reduction in Force (RIF) may be eligible for severance pay under specific conditions. To qualify, you must:
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Be a career or career-conditional employee with at least 12 months of continuous federal civilian service"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Be separated involuntarily from federal service, not for misconduct, inefficiency, or voluntary reasons"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Not be eligible for an immediate federal annuity (retirement)"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Not refuse a reasonable job offer from your agency"
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Not be receiving injury compensation from the Office of Workers' Compensation Programs"
          />
        </ListItem>
      </List>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Severance Pay Formula: Step-by-Step
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        Federal severance pay is calculated using a formula that considers your basic pay, years of service, and age. Here's a detailed breakdown of the calculation process:
      </Typography>
      
      <Box sx={{ pl: 2 }}>
        <Typography variant="h6" gutterBottom align="left">
          Step 1: Determine Your Weekly Rate
        </Typography>
        <Typography variant="body1" paragraph align="left">
          Your weekly rate is calculated by dividing your annual basic pay by 52.
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }} align="left">
          Example: If your annual salary is $75,000, your weekly rate would be $1,442.31 ($75,000 ÷ 52).
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Step 2: Calculate Basic Severance Pay
        </Typography>
        <Typography variant="body1" paragraph align="left">
          For each year of service up to 10 years, you receive one week of pay. For each year beyond 10 years, you receive two weeks of pay.
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }} align="left">
          Example: With 15 years of service, you would receive 10 weeks for the first 10 years, plus 10 weeks (5 years × 2) for the remaining 5 years, totaling 20 weeks of basic severance pay.
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Step 3: Apply the Age Adjustment Factor
        </Typography>
        <Typography variant="body1" paragraph align="left">
          If you are over 40 years old, your basic severance pay is increased by 10% for each year beyond age 40.
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }} align="left">
          Example: If you are 45 years old, your age adjustment factor would be 50% (5 years over 40 × 10%). This means your basic severance would be increased by 50%.
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Step 4: Calculate Total Severance Pay
        </Typography>
        <Typography variant="body1" paragraph align="left">
          Your total severance pay is your basic severance pay multiplied by your age adjustment factor (if applicable).
        </Typography>
        <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }} align="left">
          Example: If your basic severance is $28,846.20 (20 weeks × $1,442.31) and your age adjustment factor is 50%, your total severance would be $43,269.30 ($28,846.20 × 1.5).
        </Typography>
        
        <Typography variant="h6" gutterBottom align="left">
          Step 5: Apply the Maximum Limitation
        </Typography>
        <Typography variant="body1" paragraph align="left">
          Total severance pay cannot exceed 52 weeks of pay, regardless of years of service or age.
        </Typography>
      </Box>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Payment Schedule and Taxation
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        Severance payments are typically distributed in regular installments at the same frequency as your normal pay (usually biweekly). Payments continue until your total severance fund is exhausted.
      </Typography>
      
      <Typography variant="body1" paragraph align="left">
        These payments are subject to standard payroll taxes, including federal income tax, state income tax (where applicable), Social Security, and Medicare. They are reported on your W-2 form as regular wages.
      </Typography>
      
      <Typography variant="h5" gutterBottom sx={{ mt: 4 }} align="left">
        Special Considerations
      </Typography>
      
      <List sx={{ pl: 2 }}>
        <ListItem>
          <ListItemText 
            primary="Reemployment Impact" 
            secondary="If you're reemployed by the federal government while receiving severance pay, your payments will stop. If you're reemployed at a lower salary, you may receive a partial payment."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Military Service Credit" 
            secondary="For RIF severance calculations, federal civilian service includes active military service if you're a retired member of a uniformed service."
          />
        </ListItem>
        <ListItem>
          <ListItemText 
            primary="Retroactive Annuity" 
            secondary="If you receive severance pay and later qualify for a retroactive annuity covering the same period, you'll need to repay the severance amount."
          />
        </ListItem>
      </List>
      
      <Divider sx={{ my: 3 }} />
      
      <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 1 }} align="left">
        Note: While this article provides a comprehensive overview of federal severance pay calculations, individual circumstances may vary. Always consult with your agency's HR department or OPM for guidance specific to your situation.
      </Typography>
      
      <Typography variant="body2" align="left">
        References: OPM's Guide to Severance Pay, 5 U.S.C. §§ 5595, and 5 CFR Part 550, subpart G.
      </Typography>
    </Paper>
  );
};

export default SeveranceExplained; 